import { cn } from "@base/lib/utils";
import { useImageWithThumbnail } from "@base/model/hooks/useImageCache";
import { Image } from "@base/ui/Image";
import { ImageOff, X } from "lucide-react";
import { useEffect, useState } from "react";

interface ImagePreviewProps {
  src: string;
  /** 서버 생성 썸네일(WebP) URL. 있으면 원본(HEIC 가능성)보다 우선 표시. */
  thumbnailSrc?: string;
  /** 원본 src 가 HEIC/HEIF 이면 thumbnail fallback 이후 변환 경로를 강제한다. */
  isHeic?: boolean;
  /** 업로드 직후 서버 썸네일/원본이 준비되기 전까지 유지할 클라이언트 미리보기. */
  localPreviewSrc?: string;
  order: number;
  onLocalPreviewReady?: () => void;
  onRemove: () => void;
  className?: string;
}

/**
 * 원격 URL 한 개를 표시. (드래프트 복원, 저장된 이미지 등)
 *
 * 책임은 두 개로 분리되어 있다:
 * - retry / fallback / 캐시 구독 → {@link useImageWithThumbnail}
 * - DOM 렌더링 + 컨트롤 (order badge / Remove 버튼) → 이 컴포넌트
 *
 * 업로드 진행 중 항목은 별도의 `ImageUploadPreview` 가 담당한다.
 */
export function ImagePreview({
  src,
  thumbnailSrc,
  isHeic,
  localPreviewSrc,
  order,
  onLocalPreviewReady,
  onRemove,
  className,
}: ImagePreviewProps) {
  const [hasError, setHasError] = useState(false);
  const { src: displaySrc, isLoading, error } = useImageWithThumbnail(src, thumbnailSrc, { isHeic });
  const isRemoteError = (!isLoading && !displaySrc) || hasError || !!error;
  const shouldShowLocalPreview = !!localPreviewSrc && !displaySrc;
  const isError = isRemoteError && !shouldShowLocalPreview;

  useEffect(() => {
    if (!localPreviewSrc || !displaySrc) return;
    onLocalPreviewReady?.();
  }, [displaySrc, localPreviewSrc, onLocalPreviewReady]);

  return (
    <div className={cn("overflow-hidden relative w-full rounded-8", className)}>
      {isError ? (
        <div className="flex items-center justify-center w-full bg-gray-100 aspect-square">
          <ImageOff className="w-6 h-6 text-gray-300" />
        </div>
      ) : shouldShowLocalPreview ? (
        <Image
          src={localPreviewSrc}
          alt=""
          aspectRatio="1/1"
          className="block object-cover w-full aspect-square"
          draggable={false}
          isHeic={isHeic}
          reportCacheError={false}
        />
      ) : displaySrc ? (
        <Image
          src={displaySrc}
          alt=""
          aspectRatio="1/1"
          className="block object-cover w-full aspect-square"
          draggable={false}
          onCacheError={() => setHasError(true)}
          onError={() => setHasError(true)}
        />
      ) : (
        <div className="w-full aspect-square skeleton-shimmer" />
      )}
      <span className="absolute w-6 h-6 left-[5px] top-[5px] inline-flex items-center justify-center rounded-full bg-white">
        <span className="text-gray-1000 text-caption1">{order}</span>
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute text-gray-500 top-1 right-1"
        aria-label="Remove image"
      >
        <X />
      </button>
    </div>
  );
}
